/**
 * Strict parser and serializer for PROJECT.md, the single source of truth for
 * a project's goal and task list.
 *
 * Canonical form:
 *
 *     <one line goal>
 *
 *     ## Tasks
 *     - [ ] a task that is not started
 *     - [>] the one task in progress
 *     - [x] a task that is done
 *
 * Task markers: `- [ ]` todo, `- [>]` in progress, `- [x]` done. Claude Code
 * style: exactly one task in progress at a time (enforced by `normalizeProjectMd`).
 *
 * All functions here are pure: no I/O, no globals, no mutation of inputs.
 */
export const TASK_STATES = ['todo', 'in_progress', 'done'];
/** Thrown when input does not match the strict PROJECT.md grammar. */
export class ProjectMdError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ProjectMdError';
    }
}
const TASKS_HEADER = '## Tasks';
/** Marker text (inside the brackets) for each state. */
const STATE_TO_MARKER = {
    todo: ' ',
    in_progress: '>',
    done: 'x',
};
const MARKER_TO_STATE = {
    ' ': 'todo',
    '>': 'in_progress',
    x: 'done',
};
/** Matches exactly one task line, capturing the marker char and the caption. */
const TASK_LINE = /^- \[( |>|x)\] (.+?)\s*$/;
export function markerForState(state) {
    return STATE_TO_MARKER[state];
}
export function stateForMarker(marker) {
    return MARKER_TO_STATE[marker];
}
/**
 * Parse PROJECT.md text into structured form. Strict: the goal line and a
 * `## Tasks` header are required, and every non-blank line in the tasks section
 * must be a valid task line.
 */
export function parseProjectMd(text) {
    const lines = text.split(/\r?\n/);
    // Find the goal: first non-blank line.
    let i = 0;
    while (i < lines.length && lines[i].trim() === '')
        i++;
    if (i >= lines.length) {
        throw new ProjectMdError('PROJECT.md is empty: a goal line is required.');
    }
    const goal = lines[i].trim();
    i++;
    // Find the tasks header.
    let headerIndex = -1;
    for (let j = i; j < lines.length; j++) {
        const trimmed = lines[j].trim();
        if (trimmed === '')
            continue;
        if (trimmed === TASKS_HEADER) {
            headerIndex = j;
            break;
        }
        throw new ProjectMdError(`Unexpected line before the "${TASKS_HEADER}" header: ${JSON.stringify(lines[j])}`);
    }
    if (headerIndex === -1) {
        throw new ProjectMdError(`PROJECT.md is missing the "${TASKS_HEADER}" header.`);
    }
    // Parse task lines.
    const tasks = [];
    for (let j = headerIndex + 1; j < lines.length; j++) {
        const raw = lines[j];
        if (raw.trim() === '')
            continue;
        const m = TASK_LINE.exec(raw);
        if (!m) {
            throw new ProjectMdError(`Malformed task line: ${JSON.stringify(raw)}`);
        }
        const state = MARKER_TO_STATE[m[1]];
        if (!state) {
            throw new ProjectMdError(`Unknown task marker: ${JSON.stringify(m[1])}`);
        }
        tasks.push({ index: tasks.length, state, caption: m[2] });
    }
    return { goal, tasks };
}
/**
 * Serialize structured form back into canonical PROJECT.md text. Indices are
 * ignored on output (order is authoritative). Ends with a single trailing newline.
 */
export function serializeProjectMd(md) {
    const goal = md.goal.trim();
    const body = md.tasks
        .map((t) => `- [${STATE_TO_MARKER[t.state]}] ${t.caption.trim()}`)
        .join('\n');
    const parts = [goal, '', TASKS_HEADER];
    if (body)
        parts.push(body);
    return parts.join('\n') + '\n';
}
/** How many tasks are marked in progress. */
export function countInProgress(md) {
    return md.tasks.reduce((n, t) => (t.state === 'in_progress' ? n + 1 : n), 0);
}
/**
 * Enforce the "exactly one in progress" rule and re-index. Returns a new object;
 * the input is not mutated.
 *
 * Rules:
 *  - If more than one task is in progress, the first stays in progress and the
 *    rest drop back to todo.
 *  - If no task is in progress and at least one todo remains, the first todo is
 *    promoted to in progress.
 *  - If every task is done (nothing to work on), zero in progress is left as is.
 */
export function normalizeProjectMd(md) {
    let seenInProgress = false;
    const tasks = md.tasks.map((t, index) => {
        if (t.state === 'in_progress') {
            if (seenInProgress) {
                return { index, state: 'todo', caption: t.caption.trim() };
            }
            seenInProgress = true;
            return { index, state: 'in_progress', caption: t.caption.trim() };
        }
        return { index, state: t.state, caption: t.caption.trim() };
    });
    if (!seenInProgress) {
        const firstTodo = tasks.find((t) => t.state === 'todo');
        if (firstTodo)
            firstTodo.state = 'in_progress';
    }
    return { goal: md.goal.trim(), tasks };
}
/** Build a fresh PROJECT.md from a goal and an ordered list of task captions. */
export function createProjectMd(goal, captions) {
    const tasks = captions.map((caption, index) => ({
        index,
        state: 'todo',
        caption: caption.trim(),
    }));
    return normalizeProjectMd({ goal: goal.trim(), tasks });
}
//# sourceMappingURL=project-md.js.map